import { useState, useEffect } from 'react';
import { 
  getRecentTransactionsAPI,
  RecentTransaction,
  getRecentActivityAPI,
  RecentActivity
} from '../services/api';

export interface DashboardData {
  recentTransactions: RecentTransaction[];
  recentActivity: RecentActivity[];
  loading: boolean;
  error: string | null;
}

export const useDashboardData = (): DashboardData => {
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch recent transactions and recent activity in parallel
        const [transactionsResponse, activityResponse] = await Promise.all([
          getRecentTransactionsAPI({ pageNo: 1, pageSize: 5, searchText: '',isActive:undefined }),
          getRecentActivityAPI({ pageNo: 1, pageSize: 5, searchText: '',isActive:undefined, actionType:'',entityType:'' }),
        ]);

        // Handle recent transactions
        if (transactionsResponse.isSuccess && transactionsResponse.data) {
          let transactions = transactionsResponse.data;
          
          // Handle case where data might be wrapped in another property
          if (!Array.isArray(transactions) && transactions && typeof transactions === 'object') {
            transactions = Object.values(transactions).flat() as RecentTransaction[];
          }
          
          // Ensure transactions is an array
          if (!Array.isArray(transactions)) {
            transactions = [];
          }
          
          setRecentTransactions(transactions.slice(0, 5));
          console.log('Recent transactions fetched:', transactions);
        } else {
          console.warn('Failed to fetch recent transactions:', transactionsResponse.message);
        }

        // Handle recent activity
        if (activityResponse.isSuccess && activityResponse.data) {
          let activity = activityResponse.data;
          
          // Handle case where data might be wrapped in another property
          if (!Array.isArray(activity) && activity && typeof activity === 'object') {
            activity = Object.values(activity).flat() as RecentActivity[];
          }
          
          // Ensure activity is an array
          if (!Array.isArray(activity)) {
            activity = [];
          }
          
          setRecentActivity(activity.slice(0, 5));
          console.log('Recent activity fetched:', activity);
        } else {
          console.warn('Failed to fetch recent activity:', activityResponse.message);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard data';
        setError(errorMessage);
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return {
    recentTransactions,
    recentActivity,
    loading,
    error,
  };
};
